// src/features/admin/components/AdminSettings.jsx
import React, { useState, useEffect, useCallback } from "react";
import SchoolsTableSection from "./SchoolsTableSection";
import {
  Settings,
  BookOpen,
  School as SchoolIcon,
  Edit2,
  Save,
  Search,
  Info,
  TrendingUp,
  Lightbulb,
  Plus,
} from "lucide-react";
import { BASE_URL } from "shared/lib/config";
import { toast } from "react-toastify";

/* =========================
 * Auth helpers
 * ========================= */
function getStore() {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
}

function getAuthHeaders() {
  const store = getStore();
  const token = store.getItem("idToken");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/* =========================
 * Utils
 * ========================= */
function sortByNameAsc(list) {
  return [...list].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "id", {
      sensitivity: "base",
    })
  );
}

/* =========================================================
 * Confirm Modal (prettier than window.confirm)
 * ======================================================= */
function ConfirmModal({
  open,
  title,
  message,
  confirmText = "Ya",
  cancelText = "Batal",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <div className="px-6 py-4 text-sm text-gray-700 whitespace-pre-line">
          {message}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            {cancelText}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [activeSection, setActiveSection] = useState("statements");

  const [statements, setStatements] = useState([]);
  const [schools, setSchools] = useState([]);

  const [loadingStatements, setLoadingStatements] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);

  const [editingStatement, setEditingStatement] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  /* =========================
   * Confirm modal state (promise-based)
   * ========================= */
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    title: "Konfirmasi",
    message: "",
    confirmText: "Ya, Hapus",
    cancelText: "Batal",
    resolve: null,
  });

  const confirmDelete = useCallback(
    ({
      title = "Konfirmasi",
      message = "",
      confirmText = "Ya, Hapus",
      cancelText = "Batal",
    } = {}) =>
      new Promise((resolve) => {
        setDeleteModal({
          open: true,
          title,
          message,
          confirmText,
          cancelText,
          resolve,
        });
      }),
    []
  );

  const closeDeleteModal = useCallback((value) => {
    setDeleteModal((prev) => {
      prev.resolve?.(value);
      return { ...prev, open: false, resolve: null };
    });
  }, []);

  // ================================
  // Fetch data from backend
  // ================================
  useEffect(() => {
    const fetchStatements = async () => {
      try {
        setLoadingStatements(true);
        const res = await fetch(`${BASE_URL}/api/admin/assessment-statements`, {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });

        if (!res.ok) throw new Error(`Gagal memuat statements (${res.status})`);

        const json = await res.json();
        setStatements(Array.isArray(json.data) ? json.data : json);
      } catch (err) {
        console.error("[AdminSettings] Error fetching statements:", err);
        toast.error("Gagal memuat statement asesmen");
      } finally {
        setLoadingStatements(false);
      }
    };

    const fetchSchools = async () => {
      try {
        setLoadingSchools(true);
        const res = await fetch(`${BASE_URL}/api/admin/schools`, {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        });

        if (!res.ok) throw new Error(`Gagal memuat sekolah (${res.status})`);

        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : json;

        setSchools(sortByNameAsc(list));
      } catch (err) {
        console.error("[AdminSettings] Error fetching schools:", err);
        toast.error("Gagal memuat sekolah/distributor");
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchStatements();
    fetchSchools();
  }, []);

  // ================================
  // Filter (search)
  // ================================
  const filteredStatements = statements.filter((s) => {
    const term = searchTerm.toLowerCase();
    return (
      s.subtestName?.toLowerCase().includes(term) ||
      s.key?.toLowerCase().includes(term) ||
      s.chcCode?.toLowerCase().includes(term)
    );
  });

  const filteredSchools = schools.filter((s) => {
    const term = searchTerm.toLowerCase();
    return s.name?.toLowerCase().includes(term) || s.code?.toLowerCase().includes(term);
  });

  // ================================
  // CRUD handlers – STATEMENTS
  // ================================
  const handleUpdateStatement = async (updatedData) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/admin/assessment-statements/${updatedData._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updatedData),
        }
      );

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `Gagal menyimpan (${res.status})`);

      const updated = json && json.data && json.data._id ? json.data : updatedData;

      setStatements((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
      setEditingStatement(null);

      // ✅ match toast style from AssessmentResultsManager
      toast.success("Statement berhasil disimpan");
    } catch (err) {
      console.error("[AdminSettings] Error updating statement:", err);
      toast.error(err.message || "Gagal menyimpan statement");
    }
  };

  // ================================
  // CRUD handlers – SCHOOLS
  // ================================
  const handleUpdateSchool = async (id, updatedData) => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/schools/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(updatedData),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `Error ${res.status}`);

      const updated = json.data;

      setSchools((prev) => sortByNameAsc(prev.map((s) => (s._id === id ? updated : s))));
      toast.success("Sekolah/Distributor berhasil diperbarui");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Gagal memperbarui sekolah/distributor");
    }
  };

  const handleAddSchool = async (newSchool) => {
    try {
      const payload = { name: newSchool.name, code: newSchool.code };

      const res = await fetch(`${BASE_URL}/api/admin/schools`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `Gagal menambah sekolah (${res.status})`);

      const created =
        json && json.data && json.data._id
          ? json.data
          : {
              _id: String(Date.now()),
              ...payload,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

      setSchools((prev) => sortByNameAsc([...prev, created]));
      setShowAddForm(false);

      toast.success("Sekolah/Distributor berhasil ditambahkan");
    } catch (err) {
      console.error("[AdminSettings] Error adding school:", err);
      toast.error(err.message || "Gagal menambah sekolah/distributor");
    }
  };

  const handleDeleteSchool = async (id) => {
    const ok = await confirmDelete({
      title: "Hapus Sekolah/Distributor?",
      message:
        "Apakah Anda yakin ingin menghapus sekolah/distributor ini?\nTindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      cancelText: "Batal",
    });

    if (!ok) return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/schools/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `Gagal menghapus sekolah (${res.status})`);

      setSchools((prev) => prev.filter((s) => s._id !== id));
      toast.success("Sekolah/Distributor berhasil dihapus");
    } catch (err) {
      console.error("[AdminSettings] Error deleting school:", err);
      toast.error(err.message || "Gagal menghapus sekolah/distributor");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-blue-100 rounded-xl">
          <Settings className="w-6 h-6 text-[#4F7CFF]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Admin Settings</h2>
          <p className="text-sm text-gray-600">
            Kelola pernyataan asesmen dan data sekolah/distributor (langsung dari database)
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-50 rounded-xl p-1.5 inline-flex gap-1">
        <button
          type="button"
          onClick={() => {
            setActiveSection("statements");
            setEditingStatement(null);
            setSearchTerm("");
            setShowAddForm(false);
          }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeSection === "statements"
              ? "bg-white text-[#4F7CFF] shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Statement Asesmen
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveSection("schools");
            setEditingStatement(null);
            setSearchTerm("");
            setShowAddForm(false);
          }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeSection === "schools"
              ? "bg-white text-[#4F7CFF] shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <SchoolIcon className="w-4 h-4" />
          Sekolah/Distributor
        </button>
      </div>

      {/* Search & actions */}
      {activeSection === "statements" && !editingStatement && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cari statement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] transition-all"
          />
        </div>
      )}

      {activeSection === "schools" && (
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cari sekolah/distributor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#4F7CFF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all shadow-sm hover:shadow"
          >
            <Plus className="w-4 h-4" />
            Tambah Sekolah/Distributor
          </button>
        </div>
      )}

      {/* Content */}
      <div className="space-y-4">
        {activeSection === "statements" ? (
          loadingStatements && !editingStatement ? (
            <div className="text-sm text-gray-500">Memuat data statement asesmen...</div>
          ) : editingStatement ? (
            <EditStatementForm
              statement={editingStatement}
              onSave={handleUpdateStatement}
              onCancel={() => setEditingStatement(null)}
            />
          ) : (
            <StatementsListView statements={filteredStatements} onEdit={setEditingStatement} />
          )
        ) : loadingSchools ? (
          <div className="text-sm text-gray-500">Memuat data sekolah/distributor...</div>
        ) : (
          <SchoolsTableSection
            schools={filteredSchools}
            onUpdate={handleUpdateSchool}
            onDelete={handleDeleteSchool}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
            onAdd={handleAddSchool}
          />
        )}
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        open={deleteModal.open}
        title={deleteModal.title}
        message={deleteModal.message}
        confirmText={deleteModal.confirmText}
        cancelText={deleteModal.cancelText}
        onCancel={() => closeDeleteModal(false)}
        onConfirm={() => closeDeleteModal(true)}
      />
    </div>
  );
}

/* =========================================================
 * Statements list
 * ======================================================= */
function StatementsListView({ statements, onEdit }) {
  if (!statements || statements.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Tidak ada statement yang ditemukan</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {statements.map((statement) => (
        <div
          key={statement._id}
          onClick={() => onEdit(statement)}
          className="bg-white rounded-xl border border-gray-200 hover:border-[#4F7CFF] hover:shadow-lg hover:shadow-blue-500/10 transition-all p-5 cursor-pointer group"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-[#4F7CFF] transition-colors">
                  {statement.subtestName}
                </h3>
                <span className="px-2.5 py-0.5 bg-blue-100 text-[#4F7CFF] text-xs font-medium rounded-full">
                  {statement.chcCode}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-1">{statement.chcName}</p>
              <p className="text-xs text-gray-500">Key: {statement.key}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400 group-hover:text-[#4F7CFF] transition-colors">
              <Edit2 className="w-4 h-4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================
 * Edit Statement Form
 * ======================================================= */
function EditStatementForm({ statement, onSave, onCancel }) {
  const [formData, setFormData] = useState({ ...statement });
  const [activeTab, setActiveTab] = useState("basic");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg text-gray-900">Edit Statement</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {formData.subtestName} ({formData.chcCode})
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-60"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#4F7CFF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {saving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-b from-gray-50 to-white px-6 py-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("basic")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === "basic"
                ? "bg-[#4F7CFF] text-white shadow-md shadow-blue-500/20"
                : "bg-white text-gray-600 hover:text-[#4F7CFF] hover:bg-blue-50 border border-gray-200"
            }`}
          >
            <Info className="w-4 h-4" />
            Informasi Dasar
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("performance")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === "performance"
                ? "bg-[#4F7CFF] text-white shadow-md shadow-blue-500/20"
                : "bg-white text-gray-600 hover:text-[#4F7CFF] hover:bg-blue-50 border border-gray-200"
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Performance Statements
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("additional")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === "additional"
                ? "bg-[#4F7CFF] text-white shadow-md shadow-blue-500/20"
                : "bg-white text-gray-600 hover:text-[#4F7CFF] hover:bg-blue-50 border border-gray-200"
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            Informasi Tambahan
          </button>
        </div>
      </div>

      <div className="p-6 max-h-[600px] overflow-y-auto">
        {activeTab === "basic" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Subtest Name
                </label>
                <input
                  type="text"
                  value={formData.subtestName}
                  onChange={(e) => setFormData({ ...formData, subtestName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  CHC Name
                </label>
                <input
                  type="text"
                  value={formData.chcName}
                  onChange={(e) => setFormData({ ...formData, chcName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Strengths
              </label>
              <textarea
                value={formData.strengths}
                onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
                placeholder="Jelaskan kekuatan anak dalam kemampuan ini..."
              />
            </div>
          </div>
        )}

        {activeTab === "performance" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Unggul</label>
              <textarea
                value={formData.statements?.unggul || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    statements: { ...(formData.statements || {}), unggul: e.target.value },
                  })
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Di Atas Rata-Rata
              </label>
              <textarea
                value={formData.statements?.diatasRataRata || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    statements: {
                      ...(formData.statements || {}),
                      diatasRataRata: e.target.value,
                    },
                  })
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Rata-Rata</label>
              <textarea
                value={formData.statements?.rataRata || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    statements: { ...(formData.statements || {}), rataRata: e.target.value },
                  })
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Di Bawah Rata-Rata
              </label>
              <textarea
                value={formData.statements?.dibawahRataRata || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    statements: {
                      ...(formData.statements || {}),
                      dibawahRataRata: e.target.value,
                    },
                  })
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>
          </div>
        )}

        {activeTab === "additional" && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Learning Style (Gaya Belajar)
              </label>
              <textarea
                value={formData.learningStyle || ""}
                onChange={(e) => setFormData({ ...formData, learningStyle: e.target.value })}
                rows={5}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Home Learning Tips (Tips Belajar di Rumah)
              </label>
              <textarea
                value={formData.homeLearningTips || ""}
                onChange={(e) =>
                  setFormData({ ...formData, homeLearningTips: e.target.value })
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Extracurricular Recommendations (Rekomendasi Ekstrakurikuler)
              </label>
              <textarea
                value={formData.extracurricularRecommendations || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    extracurricularRecommendations: e.target.value,
                  })
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Minat &amp; Bakat Academic (Minat Akademik)
              </label>
              <textarea
                value={formData.minatBakatAcademic || ""}
                onChange={(e) =>
                  setFormData({ ...formData, minatBakatAcademic: e.target.value })
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF] resize-none"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
